import { useAuth0 } from "@auth0/auth0-react";
import { LoadingButton } from "@mui/lab";
import { Alert, FormControl, Grid, TextField } from "@mui/material";
import * as React from "react";
import { IMatch, IRound, ITournament } from "../../interfaces/tournamentModels";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import PublishIcon from "@mui/icons-material/Publish";
import { pairBoule, pairRound } from "../../../services/tournamentService";
import RoundStatus from "../../enums/roundStatus";
import Match from "./match";
import commonStyles from "../../styles/common.module.scss";
import MatchStatus from "../../enums/matchStatus";
import { TOURNAMENT } from "../../consts/tournamentConsts";
import YesNoModal from "../../components/yesNoModal";
import ManualPair from "./manualPair";
import FindFieldModal from "../../components/findFieldModal";
import { isClubOrganizerOrAdmin } from "../../../services/authorizationService";
import store from "../../../stores/eventStore";
import TournamentFormat from "../../enums/tournamentFormat";
import { printTeamName } from "../../utils/teamHelper";

interface RoundProps {
  tournamentData: ITournament;
  roundNumber: number;
  lastFinishedRound: number;
  onEdit: (round: number, data: IRound) => void;
}

const Round: React.FC<RoundProps> = (props) => {
  const { isAuthenticated, user } = useAuth0();

  const initialRoundData = props.tournamentData.rounds.find(
    (r) => r.roundNumber === props.roundNumber
  );

  const [loading, setLoading] = React.useState(false);
  const [updating, setUpdating] = React.useState(false);
  const [roundData, setRound] = React.useState<IRound | undefined>(initialRoundData);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [canBeCancelled, setCanBeCancelled] = React.useState<boolean>(
    props.tournamentData.rounds.findIndex(
      (r) => r.roundNumber === props.roundNumber + 1
    ) === -1 ||
      props.tournamentData.rounds.find(
        (r) => r.roundNumber === props.roundNumber + 1
      )?.status === RoundStatus.notStarted
  );
  const [showConfirmCancel, setShowConfirmCancel] =
    React.useState<boolean>(false);
  const [showManualPair, setShowManualPair] = React.useState<boolean>(false);
  const [showFindFieldModal, setShowFindFieldModal] =
    React.useState<boolean>(false);
  const [roundTime, setRoundTime] = React.useState<string>("");

  // Delayed field publishing state
  const [showPublishFieldsQuestion, setShowPublishFieldsQuestion] =
    React.useState(false);
  const [pendingMatches, setPendingMatches] = React.useState<IMatch[]>([]);
  const [publishError, setPublishError] = React.useState<string>("");

  // localMatches holds field assignments visible only to the organizer
  // (not yet saved to blob) while in fieldsPublishingMode.
  // Persisted to localStorage so navigation away and back doesn't wipe them.
  const localStorageKey = (roundNumber: number) =>
    `pendingFields_${store.eventId}_${props.tournamentData.name}_r${roundNumber}`;

  const readLocalStorage = (roundNumber: number, fallback: IMatch[]): IMatch[] => {
    try {
      const raw = localStorage.getItem(localStorageKey(roundNumber));
      if (raw) {
        const parsed = JSON.parse(raw) as IMatch[];
        if (parsed.length > 0) return parsed;
      }
    } catch {}
    return fallback;
  };

  const [localMatches, setLocalMatchesRaw] = React.useState<IMatch[]>(() => {
    if (!initialRoundData?.fieldsPublishingMode) return [];
    return readLocalStorage(props.roundNumber, [...initialRoundData.matches]);
  });

  // Wrapper that keeps localStorage in sync with the React state
  // and clears any publish error so feedback stays relevant
  const setLocalMatches = (
    matchesOrFn: IMatch[] | ((prev: IMatch[]) => IMatch[])
  ) => {
    const key = localStorageKey(props.roundNumber);
    setLocalMatchesRaw((prev) => {
      const next =
        typeof matchesOrFn === "function" ? matchesOrFn(prev) : matchesOrFn;
      if (next.length > 0) {
        localStorage.setItem(key, JSON.stringify(next));
      } else {
        localStorage.removeItem(key);
      }
      return next;
    });
    setPublishError("");
  };

  const isFieldsPublishingMode = roundData?.fieldsPublishingMode === true;

  // Returns conflict descriptions for a match (uses published rounds only)
  const getFieldConflicts = (match: IMatch): string[] => {
    if (!match.fieldNumber) return [];
    const conflicts: string[] = [];

    // Warning: another match in this round already uses the same field number
    const duplicateInRound = localMatches.filter(
      (lm) => lm.matchId !== match.matchId && lm.fieldNumber === match.fieldNumber
    );
    if (duplicateInRound.length > 0) {
      conflicts.push(`Kenttä ${match.fieldNumber} on jo käytössä tällä kierroksella`);
    }
    const allRounds = props.tournamentData.rounds;
    const prevRound = allRounds.find(
      (r) => r.roundNumber === props.roundNumber - 1
    );

    const checkTeam = (teamId: string) => {
      if (teamId === TOURNAMENT.BYE_ID) return;
      const teamObj = props.tournamentData.teams.teams.find(
        (t) => t.teamId === teamId
      );
      const teamName = teamObj ? printTeamName(teamObj) : teamId;

      // Warning 1: team played the same field in the previous round
      if (prevRound) {
        const prevConflict = prevRound.matches.find(
          (m) =>
            (m.team1Id === teamId || m.team2Id === teamId) &&
            m.fieldNumber === match.fieldNumber
        );
        if (prevConflict) {
          conflicts.push(
            `${teamName} pelasi edellisellä kierroksella kentällä ${match.fieldNumber}`
          );
        }
      }

      // Warning 2: team has played the same field 2+ times in any previous round
      const prevRounds = allRounds.filter(
        (r) => r.roundNumber < props.roundNumber
      );
      const sameFieldCount = prevRounds.reduce(
        (count, r) =>
          count +
          r.matches.filter(
            (m) =>
              (m.team1Id === teamId || m.team2Id === teamId) &&
              m.fieldNumber === match.fieldNumber
          ).length,
        0
      );
      if (sameFieldCount >= 2) {
        conflicts.push(
          `${teamName} on pelannut kentällä ${match.fieldNumber} jo ${sameFieldCount} kertaa`
        );
      }
    };

    checkTeam(match.team1Id);
    if (match.team2Id !== TOURNAMENT.BYE_ID) checkTeam(match.team2Id);

    return conflicts;
  };

  // "Luo peliparit" — compute pairs and ask about field publishing
  const handleRoundPair = () => {
    setLoading(true);
    const matches =
      props.tournamentData.roundRules.format === TournamentFormat.swiss
        ? pairRound(props.roundNumber, props.tournamentData)
        : pairBoule(props.roundNumber, props.tournamentData);
    setPendingMatches(matches);
    setLoading(false);
    setShowPublishFieldsQuestion(true);
  };

  // "Kyllä" — publish fields immediately (existing behaviour)
  const handlePairWithFields = () => {
    const newRoundData: IRound = {
      matches: pendingMatches,
      name: "",
      roundNumber: props.roundNumber,
      status: RoundStatus.paired,
      startingTime: roundTime,
      fieldsPublishingMode: false,
    };
    setRound(newRoundData);
    setLocalMatches([]);
    props.onEdit(props.roundNumber, newRoundData);
    setShowPublishFieldsQuestion(false);
  };

  // "Ei" — save pairs without field numbers; enter field publishing mode
  const handlePairWithoutFields = () => {
    // Keep field assignments locally for the organizer only
    setLocalMatches(pendingMatches);
    // Strip field numbers before saving to blob
    const strippedMatches = pendingMatches.map((m) => ({
      ...m,
      fieldNumber: undefined,
    }));
    const newRoundData: IRound = {
      matches: strippedMatches,
      name: "",
      roundNumber: props.roundNumber,
      status: RoundStatus.paired,
      startingTime: roundTime,
      fieldsPublishingMode: true,
    };
    setRound(newRoundData);
    props.onEdit(props.roundNumber, newRoundData);
    setShowPublishFieldsQuestion(false);
  };

  // "Julkaise kentät" — merge local field assignments into round data and save
  const handlePublishFields = () => {
    if (!roundData) return;

    // Validate: no two matches may share the same field number
    const fieldNumbers = localMatches
      .map((m) => m.fieldNumber)
      .filter((f): f is number => f !== undefined);
    const duplicates = fieldNumbers.filter(
      (f, i) => fieldNumbers.indexOf(f) !== i
    );
    if (duplicates.length > 0) {
      const dupeList = Array.from(new Set(duplicates)).join(", ");
      setPublishError(
        `Kenttä${duplicates.length > 1 ? "numerot" : "numero"} ${dupeList} on merkitty useammalle ottelulle. Korjaa ennen julkaisua.`
      );
      return;
    }

    const publishedMatches = roundData.matches.map((m) => {
      const local = localMatches.find((lm) => lm.matchId === m.matchId);
      return local ? { ...m, fieldNumber: local.fieldNumber } : m;
    });
    const newRoundData: IRound = {
      ...roundData,
      matches: publishedMatches,
      fieldsPublishingMode: false,
    };
    setRound(newRoundData);
    setLocalMatches([]);
    props.onEdit(props.roundNumber, newRoundData);
  };

  const handleManualPair = (matches: IMatch[]) => {
    if (isAuthenticated && isClubOrganizerOrAdmin(user, store.organizerClubs)) {
      const newRoundData: IRound = {
        matches: matches,
        name: "",
        roundNumber: props.roundNumber,
        status: RoundStatus.paired,
        startingTime: roundTime,
      };
      setRound(newRoundData);
      props.onEdit(props.roundNumber, newRoundData);
    }
  };

  const clearRound = () => {
    const newRoundData: IRound = {
      matches: [],
      name: "",
      roundNumber: props.roundNumber,
      status: RoundStatus.notStarted,
      fieldsPublishingMode: false,
    };
    setRound(newRoundData);
    setLocalMatches([]);
    props.onEdit(props.roundNumber, newRoundData);
    setShowConfirmCancel(false);
  };

  const handleMatchEdit = (matchData: IMatch) => {
    if (isFieldsPublishingMode) {
      // In field publishing mode: only update local field numbers (not saved to blob)
      setLocalMatches((prev) =>
        prev.map((m) =>
          m.matchId === matchData.matchId
            ? { ...m, fieldNumber: matchData.fieldNumber }
            : m
        )
      );
      return;
    }

    // Normal mode: update round data and mark as having unsaved changes
    const newRoundData = props.tournamentData.rounds.find(
      (r) => r.roundNumber === props.roundNumber
    )!;
    newRoundData.matches.forEach((m) => {
      if (m.matchId === matchData.matchId) {
        m.fieldNumber = matchData.fieldNumber;
        m.team1Points = matchData.team1Points;
        m.team2Points = matchData.team2Points;

        if (m.team1Points !== undefined && m.team2Points !== undefined) {
          if (m.team1Points > m.team2Points) {
            m.status = MatchStatus.team1Wins;
          } else if (m.team2Points > m.team1Points) {
            m.status = MatchStatus.team2Wins;
          } else {
            m.status = MatchStatus.notStarted;
          }
        } else {
          m.status = MatchStatus.notStarted;
        }

        if (m.team2Id === TOURNAMENT.BYE_ID) {
          m.status = MatchStatus.byeWin;
        }
      }
    });

    if (
      newRoundData.matches.every((m) => m.status !== MatchStatus.notStarted)
    ) {
      newRoundData.status = RoundStatus.completed;
    } else {
      newRoundData.status = RoundStatus.paired;
    }

    setRound(newRoundData);
    setHasChanges(true);
  };

  const handleRoundTimeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRoundTime(event.target.value);
  };

  const updateRoundData = () => {
    if (roundData) {
      props.onEdit(props.roundNumber, roundData);
      setHasChanges(false);
    }
    setUpdating(false);
  };

  React.useEffect(() => {
    const currentRoundData = props.tournamentData.rounds.find(
      (r) => r.roundNumber === props.roundNumber
    );
    setRound(currentRoundData);

    // Re-initialise localMatches when switching rounds:
    // prefer localStorage (survives navigation), fall back to round data
    if (currentRoundData?.fieldsPublishingMode) {
      const restored = readLocalStorage(
        props.roundNumber,
        [...currentRoundData.matches]
      );
      setLocalMatchesRaw(restored);
    } else {
      setLocalMatchesRaw([]);
    }

    setCanBeCancelled(
      props.tournamentData.rounds.findIndex(
        (r) => r.roundNumber === props.roundNumber + 1
      ) === -1 ||
        props.tournamentData.rounds.find(
          (r) => r.roundNumber === props.roundNumber + 1
        )?.status === RoundStatus.notStarted
    );
  }, [props.roundNumber]);

  return (
    <>
      <Grid
        item
        xs={12}
        key={`tournament-${props.roundNumber.toString()}-chosen-round`}
      >
        {roundData && roundData.status !== RoundStatus.notStarted ? (
          <>
            <p>
              Kierros {props.roundNumber} peliparit.{" "}
              {roundData.startingTime
                ? `Peliaika ${roundData.startingTime}`
                : ""}{" "}
            </p>

            <Grid
              container
              spacing={0.5}
              key={`tournament-${props.roundNumber.toString()}-pairs`}
              justifyContent="flex-start"
            >
              <Grid
                item
                xs={12}
                key={`tournament-match-find-field`}
                className={`${commonStyles.leftAlign} ${commonStyles.noPrint}`}
              >
                <LoadingButton
                  loading={loading}
                  loadingPosition="start"
                  startIcon={<SearchIcon />}
                  variant="outlined"
                  onClick={() => {
                    setShowFindFieldModal(true);
                  }}
                >
                  Hae joukkueen kenttää
                </LoadingButton>
              </Grid>
              <Grid
                item
                xs={12}
                key={`hastag`}
                className={`${commonStyles.leftAlign} ${commonStyles.noPrint}`}
              >
                <p># = Kenttänumero</p>
              </Grid>
              {isAuthenticated &&
                isClubOrganizerOrAdmin(user, store.organizerClubs) && (
                  <>
                    <Grid
                      item
                      xs={6}
                      key={`tournament-match-update`}
                      className={`${commonStyles.leftAlign} ${commonStyles.noPrint}`}
                    >
                      {isFieldsPublishingMode ? (
                        <LoadingButton
                          loading={updating}
                          loadingPosition="start"
                          startIcon={<PublishIcon />}
                          variant="contained"
                          color="primary"
                          onClick={() => {
                            setUpdating(true);
                            handlePublishFields();
                            setUpdating(false);
                          }}
                        >
                          Julkaise kentät
                        </LoadingButton>
                      ) : (
                        <LoadingButton
                          disabled={!hasChanges}
                          loading={updating}
                          loadingPosition="start"
                          startIcon={<SaveIcon />}
                          variant="outlined"
                          onClick={() => {
                            setUpdating(true);
                            updateRoundData();
                          }}
                        >
                          Vie muutokset
                        </LoadingButton>
                      )}
                    </Grid>
                    <Grid
                      item
                      xs={6}
                      key={`tournament-match-cancel`}
                      className={`${commonStyles.rightAlign} ${commonStyles.noPrint}`}
                    >
                      <LoadingButton
                        disabled={!canBeCancelled}
                        loading={updating}
                        loadingPosition="start"
                        startIcon={<SaveIcon />}
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          setShowConfirmCancel(true);
                        }}
                      >
                        Hylkää peliparit
                      </LoadingButton>
                    </Grid>
                    {publishError && (
                      <Grid
                        item
                        xs={12}
                        key={`tournament-publish-error`}
                        className={commonStyles.noPrint}
                      >
                        <Alert severity="error" onClose={() => setPublishError("")}>
                          {publishError}
                        </Alert>
                      </Grid>
                    )}
                  </>
                )}
              <Grid item xs={0.5} key={`tournament-match-field-header`} sx={{ mt: 1 }}>
                <p className={commonStyles.noEmptyTopBottom}>
                  <b>#</b>
                </p>
              </Grid>
              <Grid item xs={4} key={`tournament-match-team1-header`}>
                <p className={commonStyles.noEmptyTopBottom}></p>
              </Grid>
              <Grid item xs={1} key={`tournament-match-points1-header`}>
                <p className={commonStyles.noEmptyTopBottom}></p>
              </Grid>
              <Grid item xs={0.5} key={`tournament-match-separator-header`}>
                <p className={commonStyles.noEmptyTopBottom}></p>
              </Grid>
              <Grid item xs={1} key={`tournament-match-points2-header`}>
                <p className={commonStyles.noEmptyTopBottom}></p>
              </Grid>
              <Grid item xs={4} key={`tournament-match-team2-header`}>
                <p className={commonStyles.noEmptyTopBottom}></p>
              </Grid>
              <Grid item xs={12} key={`tournament-match-divider-header`}>
                <hr />
              </Grid>
              {roundData.matches.map((m) => {
                // In fieldsPublishingMode, use local field assignments (invisible to others)
                const effectiveMatch = isFieldsPublishingMode
                  ? (localMatches.find((lm) => lm.matchId === m.matchId) ?? m)
                  : m;
                return (
                  <Match
                    byePoints={props.tournamentData.roundRules.byePoints}
                    matchData={effectiveMatch}
                    teamData={props.tournamentData.teams}
                    key={`tournament-match-${m.matchId}`}
                    onMatchEdit={handleMatchEdit}
                    fieldsPublishingMode={isFieldsPublishingMode}
                    fieldConflicts={
                      isFieldsPublishingMode
                        ? getFieldConflicts(effectiveMatch)
                        : []
                    }
                  />
                );
              })}
            </Grid>
          </>
        ) : (
          <>
            {isAuthenticated &&
              isClubOrganizerOrAdmin(user, store.organizerClubs) &&
              props.lastFinishedRound === props.roundNumber - 1 && (
                <>
                  <Grid
                    container
                    spacing={0.5}
                    key={`tournament-${props.roundNumber.toString()}-pairing`}
                    justifyContent="flex-start"
                  >
                    {(props.tournamentData.roundRules.format ===
                      TournamentFormat.swiss ||
                      (props.tournamentData.roundRules.format ===
                        TournamentFormat.circelBoule &&
                        props.tournamentData.boules &&
                        props.tournamentData.boules.every(
                          (b) => b.teamIds.length === 4
                        ))) && (
                      <>
                        <Grid
                          item
                          xs={12}
                          key={`tournament-${props.roundNumber.toString()}-round-starting-time`}
                          className={commonStyles.leftAlign}
                        >
                          <FormControl fullWidth>
                            <TextField
                              id="roundTime"
                              label="Kierrosaika"
                              value={roundTime}
                              onChange={handleRoundTimeChange}
                            />
                          </FormControl>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          key={`tournament-${props.roundNumber.toString()}-pair-round`}
                          className={commonStyles.rightAlign}
                        >
                          <LoadingButton
                            loading={loading}
                            loadingPosition="start"
                            startIcon={<SaveIcon />}
                            variant="outlined"
                            onClick={handleRoundPair}
                          >
                            Luo peliparit
                          </LoadingButton>
                        </Grid>
                      </>
                    )}
                    {props.tournamentData.roundRules.format ===
                      TournamentFormat.circelBoule &&
                      props.tournamentData.boules &&
                      props.tournamentData.boules.some(
                        (b) => b.teamIds.length !== 4
                      ) && <p>Poulet eivät ole vielä täynnä.</p>}
                    {props.tournamentData.roundRules.format ===
                      TournamentFormat.swiss && (
                      <Grid
                        item
                        xs={6}
                        key={`tournament-${props.roundNumber.toString()}-manual-pair-round`}
                        className={commonStyles.leftAlign}
                      >
                        <LoadingButton
                          loading={loading}
                          loadingPosition="start"
                          startIcon={<SaveIcon />}
                          variant="outlined"
                          onClick={() => {
                            setShowManualPair(true);
                          }}
                        >
                          Manuaalinen paritus
                        </LoadingButton>
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
            {(!isAuthenticated ||
              !isClubOrganizerOrAdmin(user, store.organizerClubs)) &&
              props.roundNumber === 1 && (
                <p>Kierroksen pelipareja ei ole pielä muodostettu.</p>
              )}
            {props.lastFinishedRound !== props.roundNumber - 1 && (
              <p>Edellinen kierros ei ole vielä päättynyt.</p>
            )}
          </>
        )}
      </Grid>

      {/* Modal: ask whether to publish fields together with pairs */}
      <YesNoModal
        id="publish-fields-question"
        label="Halutaanko julkaista kentät samalla?"
        onCancel={() => {
          setPendingMatches([]);
          setShowPublishFieldsQuestion(false);
        }}
        onYes={handlePairWithFields}
        onNo={handlePairWithoutFields}
        open={showPublishFieldsQuestion}
      />

      <YesNoModal
        id="cancel-round"
        label="Haluatko varmasti hylätä peliparit?"
        onCancel={() => {
          setShowConfirmCancel(false);
        }}
        onNo={() => {
          setShowConfirmCancel(false);
        }}
        onYes={() => {
          clearRound();
        }}
        open={showConfirmCancel}
      />
      <ManualPair
        onCancel={() => {
          setShowManualPair(false);
        }}
        onSave={handleManualPair}
        open={showManualPair}
        tournamentData={props.tournamentData}
        roundNumber={props.roundNumber}
      />
      <FindFieldModal
        id="find-field"
        open={showFindFieldModal}
        onClose={() => {
          setShowFindFieldModal(false);
        }}
        roundData={
          isFieldsPublishingMode
            ? { ...roundData!, matches: localMatches }
            : roundData
        }
        teams={props.tournamentData.teams}
      />
    </>
  );
};

export default Round;
